import { path } from 'd3-path'
import Symbol2d from '../2d.js'


// This is used to prevent the tip from being too small. When determining this
const MIN_SEGMENT_PIXELS = 10

// This is the minimum percentage of the segment that the tip can be
const getMinSegment = dimensions => {
  return MIN_SEGMENT_PIXELS / Math.min(dimensions.width, dimensions.height)
}

/*
 * Calculates the angle of a point relative to the center of the symbol.
 * The angle is in degrees and is normalized to [0, 360]
 */
function calculateAngle ([x, y]) {
  const rad = Math.atan2(y, x)
  const deg = rad * (180 / Math.PI)
  return deg < 0 ? deg + 360 : deg
}


/*
 * Defines 4 common constraints for the tip length based on the segment it's anchored to
 * We have 8 total segments but 4 of them are just the same as the other 4, just mirrored
 * along an axis.
 *
 * Each one calculates the midpoint from the center of the symbol to the radius and then
 * determines the min or max values for the tip length
 */

const horizontalLeft = dimensions => {
  return {
    constraints: radius => {
      const midpoint = (radius + dimensions.center[0]) / 2
      // The min value is the midpoint as a percentage of the width, plus half the minimum segment percentage
      const min = (midpoint / dimensions.width) + (getMinSegment(dimensions) / 2)

      return [
        [min, .5], null
      ]
    },
    tipAdjuster: {
      idx: 0,
      multiplier: 0
    }
  }
}

const horizontalRight = dimensions => {
  return {
    constraints: radius => {
      // Determine the midpoint between the radius and the center of the symbol
      const midpoint = (dimensions.center[0] + (dimensions.width - radius)) / 2
      // The min value is the midpoint as a percentage of the width, plus half the minimum segment percentage
      const max = (midpoint / dimensions.width) - (getMinSegment(dimensions) / 2)
      return [
        [.5, max], null
      ]
    },
     tipAdjuster: {
       idx: 0,
       multiplier: 1
    }
  }
}

const verticalTop = dimensions => {
  return {
    constraints: radius => {
      const midpoint = (radius + dimensions.center[1]) / 2
      const min = (midpoint / dimensions.height) + (getMinSegment(dimensions) / 2)
      return [
        null, [min, .5]
      ]
    },
    tipAdjuster: {
      idx: 1,
      multiplier: 0
    }
  }
}

const verticalBottom = dimensions => {
  return {
    constraints: radius => {
      const midpoint = (dimensions.center[1] + (dimensions.height - radius)) / 2
      const max = (midpoint / dimensions.height) - (getMinSegment(dimensions) / 2)

      return [
        null, [.5, max]
      ]
    },
    tipAdjuster: {
      idx: 1,
      multiplier: 1
    }
  }
}

function getTipPoints (orientation, dimensions, tip, multiplierX, multiplierY, reverse) {
  if (!tip) {
    // No tip in this segment
    return
  }

  // Points for drawing the tip
  const points = []
  const offset = 0.5

  if (orientation === 'horizontal') {
    tip.y = multiplierY === -1 ? Math.min(tip.y, 0) : Math.max(tip.y, dimensions.height)
    const segmentLength = (dimensions.width / 2) - tip.radius
    const width = Math.max(segmentLength * tip.percentage, MIN_SEGMENT_PIXELS)
    const y = dimensions.center[1] + (dimensions.height * multiplierY) / 2
    const base = dimensions.center[0] + (segmentLength / 2) * multiplierX
    points.push([base - width * offset * multiplierX, y])
    points.push([tip.x, tip.y])
    points.push([base + width * offset * multiplierX, y])
  } else {
    tip.x = multiplierX === -1 ? Math.min(tip.x, 0) : Math.max(tip.x, dimensions.width)
    const segmentLength = (dimensions.height / 2) - tip.radius
    const height = Math.max(segmentLength * tip.percentage, MIN_SEGMENT_PIXELS)
    const x = dimensions.center[0] + (dimensions.width * multiplierX) / 2
    const base = dimensions.center[1] + (segmentLength  / 2) * multiplierY
    points.push([x, base - height * offset])
    points.push([tip.x, tip.y])
    points.push([x, base + height * offset])
  }

  if (reverse) {
    points.reverse()
  }

  return points
}

function drawTip (_path, points) {
  if (!points) {
    return
  }
  points.forEach(point => {
    _path.lineTo(...point)
  })
}

export default class Callout extends Symbol2d {

  handles = [{
    // First handle is the radius of the corners
    defaults: [.11, 0],
    constraints: (dimensions) => {
      const side = Math.min(dimensions.width, dimensions.height) / 2
      const max = Math.max(0, (side - MIN_SEGMENT_PIXELS) / dimensions.width)

      return [
        [0, max], null
      ]
    },
    cursor: 'all-scroll'
  }, {
    // Second handle is the position of the tip
    defaults: [.18, 1.23],
    constraints: [
      [-3, 4], [-3, 4] // The callout is allowed to be 3x the width or height of the side it's anchored to
    ],
    cursor: 'all-scroll'
  }, {
    defaults: [null, null],
    pseudo: true,
    // Third handle controls the width of the callout, based on the data.tipSize. It will be calculated dynamically based on the tip position
    constraints: (dimensions, handles) => {
      // Each segment has a different constraint for the tipSize
      const radius = handles[0].coords[0] * dimensions.width
      return this.anchored.constraints(radius)
    },
    cursor: 'all-scroll',
  }]

  constructor () {
    super(...arguments)
    this.data.tipSize = this.data.tipSize || 0.5 // Default to the middle of the segment
  }

  getPaths (data, dimensions, handles) {
    let paths = super.getPaths(data, dimensions, handles)
    paths[0].dimensions = dimensions
    return paths
  }

  onHandleChange (handle, diff, handles, dimensions) {
    if (handle.idx == 2) {
      const {multiplier, idx} = this.anchored.tipAdjuster
      const dim = idx === 0 ? 'width' : 'height'

      const midpoint = ((dimensions.center[idx] + (multiplier * (dimensions[dim] - 2 * this.radius)) + this.radius) / 2) / dimensions[dim]

      this.data.tipSize = (handle.coords[idx] - midpoint) / (.5 - midpoint)
    }
  }

  handleChangeData (handles) {
    return {
      // We only want to return the first two handles
      handles: handles.slice(0, 2).map(handle => handle.coords),
      // And the tipSize
      tipSize: this.data.tipSize
    }
  }

  getHandles (dimensions) {
    // The 3rd handle that controls the arrow length `x` value will always be
    // centered on the arrow tip
    const handles = this._getDefaultHandles()
    const tipSize = this.data.tipSize

    // When they get the handles, we need to determine the segments of the callout
    // and determine which segment the tip is on. This is needed b/c
    // the 3rd handle will be used to determine the width or height of the callout
    this.radius = this.enforceConstraints(handles[0], dimensions, handles).coords[0] * dimensions.width

    this.tip = this.getTip(dimensions, handles[1].coords, tipSize, this.radius)
    this.segments = this.getSegments(dimensions, this.radius, this.tip)
    this.anchored = this.getAnchoredSegment(this.segments, this.tip)

    this.anchored.tipPoints = this.anchored.getTipPoints(this.tip)

    // Update handle so it's locked on the tip
    // Be sure to update the object rather than creating a new one
    handles[1].coords[0] = this.tip.x / dimensions.width
    handles[1].coords[1] = this.tip.y / dimensions.height

    const tipSizeHandle = this.anchored.tipSizeHandle(this.anchored.tipPoints)
    // Again, update the object rather than creating a new one so the controls continue to work
    handles[2].coords[0] = tipSizeHandle[0] / dimensions.width
    handles[2].coords[1] = tipSizeHandle[1] / dimensions.height

    return [
      handles[0],
      handles[1],
      handles[2]
    ].map(handle => this.enforceConstraints(handle, dimensions, handles))
  }

  getTip (dimensions, handle, percentage, radius) {
    // Normalize the tip coordinates so they are relative to the center of the symbol
    const normalized = [
      dimensions.width * handle[0] - dimensions.width / 2,
      (dimensions.height * handle[1] - dimensions.height / 2) * -1, // actual y position of the tip
    ]

    return {
      x: dimensions.width * handle[0],
      y: dimensions.height / 2 - normalized[1],
      percentage, // This is the percentage of the segment length the tip is anchored to
      radius,
      angle: calculateAngle(normalized),
    }
  }

  getAnchoredSegment (segments, tip) {
    return segments.find(segment => {
      // Does this segment contain the tip
      if (tip.angle <= segment.start.angle && tip.angle >= segment.end.angle) {
        return true
      }
    })
  }

  getSegments (dimensions) {
    let segments = []

    // Start at the top center of the symbol, working right
    segments.push(Object.assign({}, horizontalRight(dimensions), {
      start: [0, dimensions.height / 2],
      end: [dimensions.width / 2, dimensions.height / 2],
      getTipPoints: tip => getTipPoints('horizontal', dimensions, tip, 1, -1),
      draw: (_path, dimensions, radius, tipPoints) => {
        _path.moveTo(dimensions.width / 2, 0)
        drawTip(_path, tipPoints)
        _path.lineTo(dimensions.width - radius, 0)
        _path.arcTo(dimensions.width, 0, dimensions.width, radius, radius)
      },
      tipSizeHandle (points) {
        return points.at(0)
      }
    }))

    segments.push(Object.assign({}, verticalTop(dimensions), {
      start: [dimensions.width / 2, dimensions.height / 2],
      end: [dimensions.width / 2, 0],
      getTipPoints: tip => getTipPoints('vertical', dimensions, tip, 1, -1),
      draw: (_path, dimensions, radius, tipPoints) => {
        drawTip(_path, tipPoints)
        _path.lineTo(dimensions.width, dimensions.center[1])
      },
      tipSizeHandle (points) {
        return points.at(-1)
      }
    }))

    segments.push(Object.assign({}, verticalBottom(dimensions), {
      start: [dimensions.width / 2, 0],
      end: [dimensions.width / 2, -dimensions.height / 2],
      getTipPoints: tip => getTipPoints('vertical', dimensions, tip, 1, 1),
      draw: (_path, dimensions, radius, tipPoints) => {
        drawTip(_path, tipPoints)
        _path.lineTo(dimensions.width, dimensions.height - radius)
        _path.arcTo(dimensions.width, dimensions.height, dimensions.width - radius, dimensions.height, radius)
      },
      tipSizeHandle (points) {
        return points.at(0)
      }
    }))

    segments.push(Object.assign({}, horizontalRight(dimensions), {
      start: [dimensions.width / 2, -dimensions.height / 2],
      end: [0, -dimensions.height / 2],
      getTipPoints: tip => getTipPoints('horizontal', dimensions, tip, 1, 1, true),
      draw: (_path, dimensions, radius, tipPoints) => {
        drawTip(_path, tipPoints)
        _path.lineTo(dimensions.width / 2,dimensions.height)
      },
      tipSizeHandle (points) {
        return points.at(-1)
      }
    }))

    segments.push(Object.assign({}, horizontalLeft(dimensions), {
      start: [0, -dimensions.height / 2],
      end: [-dimensions.width / 2, -dimensions.height / 2],
      getTipPoints: tip => getTipPoints('horizontal', dimensions, tip, -1, 1),
      draw: (_path, dimensions, radius, tipPoints) => {
        drawTip(_path, tipPoints)
        _path.lineTo(radius, dimensions.height)
        _path.arcTo(0, dimensions.height, 0, dimensions.height - radius, radius)
      },
      tipSizeHandle (points) {
        return points.at(0)
      }
    }))

    segments.push(Object.assign({}, verticalBottom(dimensions), {
      start: [-dimensions.width / 2, -dimensions.height / 2],
      end: [-dimensions.width / 2, 0],
      getTipPoints: tip => getTipPoints('vertical', dimensions, tip, -1, 1, true),
      draw: (_path, dimensions, radius, tipPoints) => {
        drawTip(_path, tipPoints)
        _path.lineTo(0, dimensions.center[1])
      },
      tipSizeHandle (points) {
        return points.at(-1)
      }
    }))

    segments.push(Object.assign({}, verticalTop(dimensions), {
      start: [-dimensions.width / 2, 0],
      end: [-dimensions.width / 2, dimensions.height / 2],
      getTipPoints: tip => getTipPoints('vertical', dimensions, tip, -1, -1, true),
      draw: (_path, dimensions, radius, tipPoints) => {
        drawTip(_path, tipPoints)
        _path.lineTo(0, radius)
        _path.arcTo(0, 0, radius, 0, radius)
      },
      tipSizeHandle (points) {
        return points.at(0)
      }
    }))

    segments.push(Object.assign({}, horizontalLeft(dimensions), {
      start: [-dimensions.width / 2, dimensions.height / 2],
      end: [0, dimensions.height / 2],
      getTipPoints: tip => getTipPoints('horizontal', dimensions, tip, -1, -1, true),
      draw: (_path, dimensions, radius, tipPoints) => {
        drawTip(_path, tipPoints)
        _path.lineTo(dimensions.center[0], 0)
      },
      tipSizeHandle (points) {
        return points.at(-1)
      }
    }))

    return segments.map(segment => {
      segment.start.angle = calculateAngle(segment.start)
      segment.end.angle = calculateAngle(segment.end)

      if (segment.start.angle == 0 && segment.end.angle > 180) {
        segment.start.angle = 360
      }

      return segment
    })
  }

  path (dimensions) {
    const _path = path()

    this.segments.forEach(segment => {
      segment.draw(_path, dimensions, this.radius, segment.tipPoints)
    })

    _path.closePath()

    return _path.toString()
  }

  // Prevent custom mitering on the callout. See
  // https://github.com/SpiderStrategies/Scoreboard/issues/57794
  getMiter () {
    return null
  }

}
